import { PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CTB from '~/components/CTB';
import { useMFEContext } from '~/components/MFEProvider';
import useFeature from '~/hooks/useFeature';
import { ResourceLimitType } from '~/scenes/Hosting/components/AddSiteButton/Validation';

const ResourcesWarningModal = ({
  resourceLimitMap = {},
  open = false,
  setOpen,
}: PropsWithChildren<{
  resourceLimitMap?: { [key: string]: ResourceLimitType };
  open?: boolean;
  setOpen: Function;
}>) => {
  // @ts-ignore
  const { t } = useTranslation('sites', {
    keyPrefix: 'cloud.resourceLimitModal',
  });
  const [canUpgrade] = useFeature('hosting_upgrade');

  const {
    // @ts-expect-error
    hostingId,
    // @ts-expect-error
    data: {
      hostingData: { productName },
    },
  } = useMFEContext();

  const handleClose = () => setOpen(false);
  const is5sHostingType = resourceLimitMap?.sites?.is5sHostingType;

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      data-testid="add-resource-warning-modal"
    >
      <DialogContent>
        <Stack direction="row" alignItems="center">
          <WarningIcon color="error" />
          <DialogTitle>{t(`title`)}</DialogTitle>
        </Stack>
        <Typography variant="body1" pb={2} component="span">
          <Trans i18nKey="list_map">
            {is5sHostingType
              ? t(`description.hostingPlan5sMaxWebsites`)
              : t(`description.part1`)}
            {/* @ts-ignore */}
            <ul i18nIsDynamicList>
              {Object.keys(resourceLimitMap)
                .filter((resource) => resourceLimitMap[resource].limitReached)
                .map((resource) => (
                  <li>
                    <Typography color="error">
                      {t(`resources.${resource}`)}
                    </Typography>
                  </li>
                ))}
            </ul>

            <Trans
              t={t}
              i18nKey={
                is5sHostingType
                  ? `description.limitedSitesUpgrade`
                  : `description.upgradeText`
              }
              components={
                canUpgrade
                  ? {
                      Link: (
                        <CTB
                          product="HOSTING_UPGRADE"
                          handleDialogClose={handleClose}
                          data={{
                            prodInstId: hostingId,
                            productName,
                          }}
                        />
                      ),
                    }
                  : { Link: <u></u> }
              }
            ></Trans>
          </Trans>
        </Typography>

        <Stack spacing={1} direction="row" justifyContent="flex-end" mt={3}>
          <Button onClick={handleClose} variant="text">
            {t('close')}
          </Button>

          {/* show only for php workers or storage */}
          {(resourceLimitMap.phpWorker.limitReached ||
            resourceLimitMap.storage.limitReached) && (
            <Button href="./resources" variant="contained">
              {t('manageResources')}
            </Button>
          )}

          {/* show only for db */}
          {resourceLimitMap.db.limitReached && (
            <Button href="./databases" variant="contained">
              {t('manageDatabases')}
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ResourcesWarningModal;
